// API service for communicating with the backend
// For production: Set REACT_APP_API_URL=https://your-backend-url.com/api
// For production: Set REACT_APP_SERVER_URL=https://your-backend-url.com
const trimTrailingSlash = (value) => String(value || "").replace(/\/+$/, "");

const resolveApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return trimTrailingSlash(process.env.REACT_APP_API_URL);
  }

  if (typeof window !== "undefined") {
    const { hostname } = window.location;
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
    if (isLocalHost) {
      return "http://localhost:5000/api";
    }
    return "/api";
  }

  return "http://localhost:5000/api";
};

const resolveServerBaseUrl = (apiBaseUrl) => {
  if (process.env.REACT_APP_SERVER_URL) {
    return trimTrailingSlash(process.env.REACT_APP_SERVER_URL);
  }

  if (process.env.REACT_APP_API_URL) {
    return trimTrailingSlash(process.env.REACT_APP_API_URL).replace(/\/api$/, "");
  }

  if (typeof window !== "undefined") {
    if (apiBaseUrl.startsWith("http")) {
      return apiBaseUrl.replace(/\/api$/, "");
    }
    return trimTrailingSlash(window.location.origin);
  }

  return "http://localhost:5000";
};

const API_BASE_URL = resolveApiBaseUrl();
const SERVER_BASE_URL = resolveServerBaseUrl(API_BASE_URL);

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.serverURL = SERVER_BASE_URL;
  }

  buildUrl(endpoint, baseUrl = this.baseURL) {
    return `${baseUrl}${endpoint}`;
  }
  
  // Helper method to get full image URL
  getImageUrl(imageUrl) {
    if (!imageUrl) return '';
    // If it's already a full URL, return as is
    if (imageUrl.startsWith('http')) return imageUrl;
    // Inline data URLs should be used directly
    if (imageUrl.startsWith('data:')) return imageUrl;
    // Otherwise, prepend server URL (works in both dev and production)
    return `${this.serverURL}${imageUrl}`;
  }

  // Get auth token from localStorage
  getToken() {
    return localStorage.getItem('authToken');
  }

  // Set auth token in localStorage
  setToken(token) {
    localStorage.setItem('authToken', token);
  }

  // Remove auth token from localStorage
  removeToken() {
    localStorage.removeItem('authToken');
  }

  // Make HTTP request with auth header
  async request(endpoint, options = {}) {
    const url = this.buildUrl(endpoint);
    const token = this.getToken();
    const isFormData = options.body instanceof FormData;
    const defaultHeaders = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    const config = {
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      const contentType = response.headers.get('content-type') || '';
      const data = contentType.includes('application/json')
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        // If we get a 401 error, the token is invalid - remove it
        if (response.status === 401) {
          console.log('Received 401 error, removing invalid token');
          const hadToken = Boolean(token);
          this.removeToken();
          const method = String(config.method || 'GET').toUpperCase();
          const isAuthRequest = endpoint.startsWith('/auth/');
          if (typeof window !== 'undefined' && method !== 'GET' && !isAuthRequest) {
            window.dispatchEvent(new CustomEvent('prepRightAuthPrompt', {
              detail: { message: 'Register or log in to use this feature.' }
            }));
          }
          // Dispatch a custom event to notify components that authentication failed
          if (hadToken) {
            window.dispatchEvent(new CustomEvent('authTokenExpired'));
          }
        }
        
        const errorMessage = typeof data === 'object' && data !== null
          ? data.message
          : `HTTP error! status: ${response.status}`;
        const error = new Error(errorMessage);
        error.response = { data, status: response.status };
        throw error;
      }

      return data;
    } catch (error) {
      if (error instanceof TypeError) {
        const errorText = String(error.message || "").toLowerCase();
        if (errorText.includes("failed to fetch") || errorText.includes("network")) {
          error.code = "NETWORK_ERROR";
        }
      }
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Authentication methods
  async register(userData) {
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    // Store token for future requests (auto-login after registration)
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async login(email, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    
    // Store token for future requests
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async logout() {
    try {
      await this.request('/auth/logout', {
        method: 'POST',
      });
    } finally {
      // Always remove token from localStorage
      this.removeToken();
    }
  }

  async verifyToken() {
    return this.request('/auth/verify');
  }

  // User methods
  async getProfile() {
    return this.request('/users/profile');
  }

  async updateProfile(profileData) {
    return this.request('/users/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  }

  async uploadProfilePicture(file) {
    const formData = new FormData();
    formData.append('profilePicture', file);

    return this.request('/upload/profile-picture', {
      method: 'POST',
      body: formData,
    });
  }

  async updateCookieConsent() {
    return this.request('/users/cookie-consent', {
      method: 'PUT',
    });
  }

  async getCookieConsent() {
    return this.request('/users/cookie-consent', {
      method: 'GET',
    });
  }

  async updateStats(statsData) {
    return this.request('/users/stats', {
      method: 'PUT',
      body: JSON.stringify(statsData),
    });
  }

  async deleteAccount() {
    const response = await this.request('/users/account', {
      method: 'DELETE',
    });
    
    // Remove token after account deletion
    this.removeToken();
    
    return response;
  }

  async getUserStats() {
    return this.request('/users/stats');
  }


  // Health check
  async healthCheck() {
    return this.request('/health');
  }

  // Activity methods
  async logActivity(activityData) {
    return this.request('/activities/log', {
      method: 'POST',
      body: JSON.stringify(activityData),
    });
  }

  async getActivities(limit = 10) {
    return this.request(`/activities?limit=${limit}`);
  }

  async getUserActivities(limit = 10) {
    return this.request(`/activities?limit=${limit}`);
  }

  async getActivityStats() {
    return this.request('/activities/stats');
  }

  async getDailyBonusStatus() {
    return this.request('/activities/daily-bonus-status');
  }

  async checkDailyPointsStatus(activityType) {
    return this.request(`/activities/check-daily-points/${activityType}`);
  }

  // Achievement methods
  async getAchievements() {
    return this.request('/achievements');
  }

  async getEarnedAchievements() {
    return this.request('/achievements/earned');
  }

  async checkAchievements() {
    return this.request('/achievements/check', {
      method: 'POST',
    });
  }

  // Feed methods
  async getFeedPosts() {
    return this.request('/feed');
  }

  async createFeedPost(formData) {
    return this.request('/feed', {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type, let browser set it for FormData
        ...(this.getToken() && { Authorization: `Bearer ${this.getToken()}` }),
      },
    });
  }

  async likeFeedPost(postId) {
    return this.request(`/feed/${postId}/like`, {
      method: 'POST',
    });
  }

  // Pantry methods
  async getPantryItems() {
    return this.request('/pantry');
  }

  async getPantryStats() {
    return this.request('/pantry/stats');
  }

  async getExpiringItems(days = 3) {
    return this.request(`/pantry/expiring?days=${days}`);
  }

  async getExpiredItems() {
    return this.request('/pantry/expired');
  }

  async addPantryItem(itemData) {
    return this.request('/pantry', {
      method: 'POST',
      body: JSON.stringify(itemData),
    });
  }

  async updatePantryItem(id, itemData) {
    return this.request(`/pantry/${id}`, {
      method: 'PUT',
      body: JSON.stringify(itemData),
    });
  }

  async deletePantryItem(id) {
    return this.request(`/pantry/${id}`, {
      method: 'DELETE',
    });
  }

  async deleteExpiredItems() {
    return this.request('/pantry/expired/bulk', {
      method: 'DELETE',
    });
  }

  async bulkDeletePantryItems(itemIds) {
    return this.request('/pantry/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ itemIds }),
    });
  }

  // Shopping Lists API methods
  async getShoppingLists() {
    return this.request('/shopping-lists');
  }

  async getShoppingListStats() {
    return this.request('/shopping-lists/stats');
  }

  async getShoppingList(id) {
    return this.request(`/shopping-lists/${id}`);
  }

  async createShoppingList(data) {
    return this.request('/shopping-lists', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateShoppingList(id, data) {
    return this.request(`/shopping-lists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteShoppingList(id) {
    return this.request(`/shopping-lists/${id}`, {
      method: 'DELETE',
    });
  }

  async addShoppingListItem(listId, data) {
    return this.request(`/shopping-lists/${listId}/items`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateShoppingListItem(itemId, data) {
    return this.request(`/shopping-lists/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteShoppingListItem(itemId) {
    return this.request(`/shopping-lists/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteShoppingLists(listIds) {
    return this.request('/shopping-lists/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ listIds }),
    });
  }

  async getEssentialsConfig() {
    return this.request('/shopping-lists/essentials/config');
  }

  async updateEssentialsConfig(essentials) {
    return this.request('/shopping-lists/essentials/config', {
      method: 'PUT',
      body: JSON.stringify({ essentials }),
    });
  }

  // Meal Plans API methods
  async getMealPlans() {
    return this.request('/meal-plans');
  }

  async getMealPlanStats() {
    return this.request('/meal-plans/stats');
  }

  async getMealPlan(id) {
    return this.request(`/meal-plans/${id}`);
  }

  async createMealPlan(data) {
    return this.request('/meal-plans', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMealPlan(id, data) {
    return this.request(`/meal-plans/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMealPlan(id) {
    return this.request(`/meal-plans/${id}`, {
      method: 'DELETE',
    });
  }

  async addMealPlanMeal(planId, data) {
    return this.request(`/meal-plans/${planId}/meals`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateMealPlanMeal(mealId, data) {
    return this.request(`/meal-plans/meals/${mealId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteMealPlanMeal(mealId) {
    return this.request(`/meal-plans/meals/${mealId}`, {
      method: 'DELETE',
    });
  }

  async bulkDeleteMealPlans(planIds) {
    return this.request('/meal-plans/bulk', {
      method: 'DELETE',
      body: JSON.stringify({ planIds }),
    });
  }

  // Recipe methods
  async getPublicRecipes(params = {}) {
    const queryParams = new URLSearchParams(params).toString();
    return this.request(`/recipes/public?${queryParams}`);
  }

  async getUserRecipes() {
    return this.request('/recipes/user/my-recipes');
  }

  async getRecipe(id) {
    return this.request(`/recipes/${id}`);
  }

  async createRecipe(recipeData) {
    return this.request('/recipes', {
      method: 'POST',
      body: JSON.stringify(recipeData),
    });
  }

  async updateRecipe(id, recipeData) {
    return this.request(`/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(recipeData),
    });
  }

  async deleteRecipe(id) {
    return this.request(`/recipes/${id}`, {
      method: 'DELETE',
    });
  }

  async likeRecipe(id) {
    return this.request(`/recipes/${id}/like`, {
      method: 'POST',
    });
  }

  async getFavoriteRecipes() {
    return this.request('/recipes/favorites');
  }

  async trackRecipeShare(recipeId, data = {}) {
    return this.request(`/recipes/${recipeId}/share`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Upload methods
  async uploadImage(imageFile) {
    const formData = new FormData();
    formData.append('image', imageFile);

    const url = `${this.baseURL}/upload/image`;
    const token = this.getToken();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || `HTTP error! status: ${response.status}`);
      error.response = { data, status: response.status };
      throw error;
    }

    return data;
  }

  async deleteImage(filename) {
    return this.request(`/upload/images/${filename}`, {
      method: 'DELETE',
    });
  }

  // Shopping List Generator API methods
  async generateShoppingListFromRecipe(recipeId, options = {}) {
    return this.request('/shopping-list-generator/from-recipe', {
      method: 'POST',
      body: JSON.stringify({
        recipeId,
        shoppingListName: options.shoppingListName,
        addToExistingList: options.addToExistingList,
        existingListId: options.existingListId
      }),
    });
  }

  async generateShoppingListFromRecipes(recipeIds, options = {}) {
    return this.request('/shopping-list-generator/from-recipes', {
      method: 'POST',
      body: JSON.stringify({
        recipeIds,
        shoppingListName: options.shoppingListName,
        addToExistingList: options.addToExistingList,
        existingListId: options.existingListId
      }),
    });
  }

  async previewShoppingList(recipeIds) {
    return this.request('/shopping-list-generator/preview', {
      method: 'POST',
      body: JSON.stringify({ recipeIds }),
    });
  }

  async getShoppingListsForGenerator() {
    return this.request('/shopping-list-generator/lists');
  }

  // Delete user profile and all associated data
  async deleteProfile() {
    return this.request('/users/delete-profile', {
      method: 'DELETE'
    });
  }

  // Give test points to user (for testing achievements)
  async giveTestPoints(points) {
    return this.request('/users/test-points', {
      method: 'POST',
      body: JSON.stringify({ points })
    });
  }

  // Sous-chef methods
  async sendAIMessage(message, conversationHistory = []) {
    return this.request('/ai/chat', {
      method: 'POST',
      body: JSON.stringify({
        message,
        conversationHistory
      }),
    });
  }

  async testAIService() {
    return this.request('/ai/test');
  }

  async getRecipeSuggestions(expiringItems) {
    const requestBody = { expiringItems };
    
    try {
      const response = await this.request('/ai/recipe-suggestions', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });
      
      return response;
    } catch (error) {
      console.error('Recipe suggestions request failed:', error);
      throw error;
    }
  }

  // Feed methods
  async getFeed() {
    return this.request('/feed');
  }

  async getMyPosts() {
    return this.request('/feed/my-posts');
  }

  async createPost(formData) {
    const token = this.getToken();
    const response = await fetch(`${this.baseURL}/feed`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || `HTTP error! status: ${response.status}`);
      error.response = { data, status: response.status };
      throw error;
    }

    return data;
  }

  async createPostSimple(postData) {
    const token = this.getToken();
    const response = await fetch(`${this.baseURL}/feed`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify(postData),
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || `HTTP error! status: ${response.status}`);
      error.response = { data, status: response.status };
      throw error;
    }

    return data;
  }

  async deletePost(postId) {
    return this.request(`/feed/${postId}`, {
      method: 'DELETE',
    });
  }

  async updatePost(postId, data) {
    return this.request(`/feed/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async likePost(postId) {
    return this.request(`/feed/${postId}/like`, {
      method: 'POST',
    });
  }

  async unlikePost(postId) {
    return this.request(`/feed/${postId}/like`, {
      method: 'POST',
    });
  }

  async addComment(postId, content) {
    return this.request(`/feed/${postId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  async deleteComment(commentId) {
    return this.request(`/feed/comments/${commentId}`, {
      method: 'DELETE',
    });
  }

  async uploadFeedImage(formData) {
    const token = this.getToken();
    const response = await fetch(`${this.baseURL}/feed/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      const error = new Error(data.message || `HTTP error! status: ${response.status}`);
      error.response = { data, status: response.status };
      throw error;
    }

    return data;
  }

  // Leaderboard methods
  async getLeaderboard(type = 'all-time', limit = 50, friends = false) {
    const cacheBust = Date.now();
    return this.request(`/leaderboard/${type}?limit=${limit}&friends=${friends}&_t=${cacheBust}`, {
      cache: 'no-store'
    });
  }

  async getMonthlyLeaderboard(limit = 50) {
    const cacheBust = Date.now();
    return this.request(`/leaderboard/monthly/detailed?limit=${limit}&_t=${cacheBust}`, {
      cache: 'no-store'
    });
  }

  // User methods
  async getAllUsers() {
    return this.request('/users/all');
  }

  async sendFriendRequest(userId) {
    return this.request('/friends/request', {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
  }

  async getFriendRequests() {
    return this.request('/friends/requests');
  }

  async acceptFriendRequest(requestId, notificationId = null) {
    return this.request('/friends/accept', {
      method: 'POST',
      body: JSON.stringify({ requestId, notificationId })
    });
  }

  async declineFriendRequest(requestId, notificationId = null) {
    return this.request('/friends/decline', {
      method: 'POST',
      body: JSON.stringify({ requestId, notificationId })
    });
  }

  async getFriends() {
    return this.request('/friends');
  }

  async removeFriend(friendId) {
    return this.request(`/friends/${friendId}`, {
      method: 'DELETE'
    });
  }

  // Notification methods
  async getNotifications() {
    return this.request('/notifications');
  }

  async markNotificationAsRead(notificationId) {
    return this.request(`/notifications/${notificationId}/read`, {
      method: 'PUT',
    });
  }

  async markAllNotificationsAsRead() {
    return this.request('/notifications/read-all', {
      method: 'PUT',
    });
  }

  async markAllNotificationsRead() {
    return this.request('/notifications/mark-all-read', {
      method: 'PUT',
    });
  }

  async clearAllNotifications() {
    return this.request('/notifications/clear-all', {
      method: 'DELETE',
    });
  }

  // ==================== Wheel Spin API ====================
  
  // Check wheel status (can spin today?)
  async checkWheelStatus() {
    return this.request('/wheel/status', {
      method: 'GET',
    });
  }

  // Spin the wheel
  async spinWheel() {
    return this.request('/wheel/spin', {
      method: 'POST',
    });
  }

  // Get active rewards (boosts, shields)
  async getActiveRewards() {
    return this.request('/wheel/active-rewards', {
      method: 'GET',
    });
  }

  // Get spin history
  async getSpinHistory() {
    return this.request('/wheel/history', {
      method: 'GET',
    });
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;
